"use client"

import { useState, useCallback } from "react"
import type { ChangeTracker } from "../types/account-details"

export const useChangeTracker = <T extends Record<string, any>>(initialData: T): ChangeTracker<T> => {
  const [data, setData] = useState<T>(initialData)
  const [originalData, setOriginalData] = useState<T>(initialData)
  const [changedFields, setChangedFields] = useState<Set<keyof T>>(new Set())

  const updateField = useCallback(
    (field: keyof T, value: any) => {
      setData((prev) => {
        const newData = { ...prev, [field]: value }

        // Track changed fields
        setChangedFields((prevChanged) => {
          const newChanged = new Set(prevChanged)
          if (originalData[field] !== value) {
            newChanged.add(field)
          } else {
            newChanged.delete(field)
          }
          return newChanged
        })

        return newData
      })
    },
    [originalData],
  )

  const getChangedData = useCallback(() => {
    const changes: Partial<T> = {}
    changedFields.forEach((field) => {
      changes[field] = data[field]
    })
    return changes
  }, [data, changedFields])

  const resetChanges = useCallback(() => {
    setData(originalData)
    setChangedFields(new Set())
  }, [originalData])

  const updateOriginalData = useCallback((newData: T) => {
    setOriginalData(newData)
    setData(newData)
    setChangedFields(new Set())
  }, [])

  return {
    data,
    changedFields,
    hasChanges: changedFields.size > 0,
    updateField,
    getChangedData,
    resetChanges,
    updateOriginalData,
  }
}
